import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICategory } from '../../models/categories.models';
import { AppState } from 'src/app/core/app.state';
import { Store } from '@ngrx/store';
import { Observable, Subject, of, take, takeUntil } from 'rxjs';
import { AddCategory, AddCategorySuccess, FetchAllCategories, UpdateCategory, UpdateCategorySuccess } from '../../store/categories.actions';
import { Actions, ofType } from '@ngrx/effects';
import { environment } from 'src/environments/environment';
import { SelectOption } from 'src/app/ui/form-field/form-field.component';
import { TranslateService } from '@ngx-translate/core';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';

@Component({
  selector: 'app-add-categories',
  templateUrl: './add-categories.component.html',
  styleUrls: ['./add-categories.component.scss']
})
export class AddCategoriesComponent implements OnInit, OnDestroy {

  form!: FormGroup;
  categories$: Observable<ICategory[]> = of([]);
  parentCategoryOptions: SelectOption[] = [];
  uploadedImage!: string;
  displayedImage!: string;
  isUpdate: boolean = false;
  canSubmit = false;
  canSubmit$: Observable<boolean> = of(false);
  baseUrl = environment.BASE_URL_API;
  timestamp = new Date().getTime();
  statusOptions: SelectOption[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<AddCategoriesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ICategory,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private actions$: Actions,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.statusOptions = [
      { value: 'Published', label: this.translate.instant('Categories.published') },
      { value: 'Draft', label: this.translate.instant('common.draft') },
    ];
    this.store.dispatch(FetchAllCategories({ includes: ['id', 'name'] }));
    this.categories$ = this.store.select((state) => state.categories.allCategories);
    this.form = this.fb.group({
      code: new FormControl(this.data?.code ? this.data?.code : '', Validators.required),
      name: new FormControl(this.data?.name ?? '', Validators.required),
      parent_id: new FormControl(this.data?.parent_id ?? null),
      description: new FormControl(this.data?.description ?? ''),
      status: new FormControl(this.data?.status ?? null),
    });
    if (this.data) {
      this.displayedImage = this.data.image!;
      this.isUpdate = true;
    }

    this.canSubmit$ = this.store.select(selectCanDo('product', this.isUpdate ? 'edit' : 'create'));
    this.canSubmit$.pipe(takeUntil(this.destroy$)).subscribe(canSubmit => {
      this.canSubmit = canSubmit;
      canSubmit ? this.form.enable({ emitEvent: false }) : this.form.disable({ emitEvent: false });
    });

    this.categories$.pipe(takeUntil(this.destroy$)).subscribe(list => {
      this.parentCategoryOptions = (list || [])
        .filter(c => !this.data || c.id !== this.data.id)
        .map(c => ({ value: c.id, label: c.name }));
    });

    this.actions$.pipe(ofType(AddCategorySuccess, UpdateCategorySuccess), take(1)).subscribe(() => this.closed());
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canSubmit) return;
    (event.currentTarget as HTMLElement).classList.add('drag-over');
  }
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.remove('drag-over');
  }
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.remove('drag-over');
    if (!this.canSubmit) return;

    const files = event.dataTransfer?.files;
    if (files) {
      this.processFiles(files);
    }
  }
  onFileChange(event: Event) {
    if (!this.canSubmit) return;
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (files) {
      this.processFiles(files);
      input.value = '';
    }
  }

  processFiles(files: FileList) {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const dataUrl = e.target.result;
      this.uploadedImage = dataUrl.split(',')[1];
    };
    reader.readAsDataURL(file);
  }

  cancelImage() {
    if (!this.canSubmit) return;
    this.uploadedImage = '';
    this.displayedImage = '';
  }

  getImage(): string {
    return this.baseUrl + this.displayedImage + '?timestamp=' + this.timestamp;
  }

  save(): boolean | void {
    if (!this.canSubmit) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return false;
    }

    const payload: Omit<ICategory, 'id'> = {
      code: this.form.get('code')?.value,
      name: this.form.get('name')?.value,
      description: this.form.get('description')?.value,
      parent_id: this.form.get('parent_id')?.value,
      status: this.form.get('status')?.value,
      image: this.uploadedImage,
    }
    if (this.data) {
      this.store.dispatch(UpdateCategory({ id: this.data.id, category: payload }));
    } else {
      this.store.dispatch(AddCategory({ category: payload }));
    }
  }

  submitPermission() {
    return { module: 'product', action: this.isUpdate ? 'edit' : 'create' };
  }

  closed() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
